import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Stats {
  totalProduk: number;
  totalPenjualan: number;
  totalPembelian: number;
  labaBersih: number;
  stokRendah: number;
}

interface ReportItem {
  label: string;
  total_sales: number;
}

const rupiahFormat = (n: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (month: string) => {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(year, monthNumber - 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
};

export default function Dashboard() {
  const { role } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [stats, setStats] = useState<Stats>({ totalProduk: 0, totalPenjualan: 0, totalPembelian: 0, labaBersih: 0, stokRendah: 0 });
  const [report, setReport] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [inv, profitRes, reportRes] = await Promise.all([
          api.get('/inventories/getAllInventories'),
          api.get(`/dashboard/monthly-profit?month=${selectedMonth}`),
          api.get('/sales/report?type=daily'),
        ]);

        const inventories = inv.data;
        const profit = profitRes.data;
        const stokRendah = inventories.filter((i: any) => i.stock <= 5).length;

        setStats({
          totalProduk: inventories.length,
          totalPenjualan: Number(profit.total_sales || 0),
          totalPembelian: Number(profit.total_purchases || 0),
          labaBersih: Number(profit.net_profit || 0),
          stokRendah,
        });
        setReport(reportRes.data.data?.slice(-7) || []);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [selectedMonth]);

  const statCards = [
    {
      label: 'Total Produk',
      value: stats.totalProduk.toString(),
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>,
      bg: 'bg-blue-50', text: 'text-blue-600',
    },
    {
      label: `Penjualan ${monthLabel(selectedMonth)}`,
      value: rupiahFormat(stats.totalPenjualan),
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>,
      bg: 'bg-green-50', text: 'text-green-600',
    },
    {
      label: `Pembelian ${monthLabel(selectedMonth)}`,
      value: rupiahFormat(stats.totalPembelian),
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
      bg: 'bg-purple-50', text: 'text-purple-600',
    },
    {
      label: `Laba Bersih ${monthLabel(selectedMonth)}`,
      value: rupiahFormat(stats.labaBersih),
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      bg: stats.labaBersih >= 0 ? 'bg-emerald-50' : 'bg-red-50',
      text: stats.labaBersih >= 0 ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'Stok Rendah',
      value: stats.stokRendah.toString(),
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
      bg: 'bg-orange-50', text: 'text-orange-600',
    },
  ];

  const maxSales = report.reduce((max, r) => Math.max(max, r.total_sales), 0) || 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-gray-500">
          <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Memuat data...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">
            Selamat datang, <span className="font-medium capitalize text-indigo-600">{role}</span>
          </p>
        </div>
        <label className="flex flex-col gap-1 text-sm text-gray-500">
          Periode laba
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-48 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${card.bg} rounded-xl flex items-center justify-center ${card.text}`}>
                {card.icon}
              </div>
              {card.label === 'Stok Rendah' && stats.stokRendah > 0 && (
                <span className="text-xs bg-red-100 text-red-600 font-semibold px-2 py-1 rounded-full">Perhatian</span>
              )}
              {card.label.startsWith('Laba Bersih') && (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${stats.labaBersih >= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                  {stats.labaBersih >= 0 ? 'Surplus' : 'Minus'}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="text-xl font-bold text-gray-800 mt-1 truncate">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-6">Penjualan 7 Hari Terakhir</h2>
          {report.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              Belum ada data penjualan
            </div>
          ) : (
            <div className="flex items-end gap-2 h-48">
              {report.map((r) => {
                const heightPct = (r.total_sales / maxSales) * 100;
                return (
                  <div key={r.label} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex items-end justify-center" style={{ height: '120px' }}>
                      <div
                        className="w-full bg-indigo-500 rounded-t-lg hover:bg-indigo-600 transition-colors"
                        style={{ height: `${Math.max(heightPct, 4)}%` }}
                        title={rupiahFormat(r.total_sales)}
                      />
                    </div>
                    <span className="text-xs text-gray-400 truncate w-full text-center">
                      {r.label.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Ringkasan */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Ringkasan</h2>
          <div className="space-y-1">
            {[
              { label: 'Total Produk', value: `${stats.totalProduk} item`, cls: 'text-gray-800' },
              { label: 'Stok Rendah (≤5)', value: `${stats.stokRendah} item`, cls: stats.stokRendah > 0 ? 'text-red-500' : 'text-green-500' },
              { label: `Penjualan ${monthLabel(selectedMonth)}`, value: rupiahFormat(stats.totalPenjualan), cls: 'text-gray-800' },
              { label: `Pembelian ${monthLabel(selectedMonth)}`, value: rupiahFormat(stats.totalPembelian), cls: 'text-gray-800' },
              { label: 'Laba Bersih', value: rupiahFormat(stats.labaBersih), cls: stats.labaBersih >= 0 ? 'text-emerald-600' : 'text-red-500' },
            ].map((row, i, arr) => (
              <div key={row.label} className={`flex items-center justify-between py-3 ${i < arr.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <span className="text-sm text-gray-500">{row.label}</span>
                <span className={`font-semibold text-sm ${row.cls}`}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
